/* eslint-disable react/no-unescaped-entities */
import React, { ReactNode } from "react";
import {
  professionalConnectFormSchema,
  iPractitionerProfile,
  pcInterests,
  pcReferrals,
  pcServices,
  pcRequests,
} from "ps-shared";
import { Formik, ErrorMessage, Field } from "formik";
import ReactGA from "react-ga";
import { Button, Col, Form, FormGroup, Row } from "reactstrap";

import { AdminService } from "src/services/AdminService";
import { FormikButtonList } from "src/components/Forms/FormikButtonList";

interface PcFormProps {
  profile: iPractitionerProfile;
  slug: string;
  cb: (isPc: boolean) => void;
}
export interface PcFormCb {
  success: boolean;
}
export const PcForm: React.FunctionComponent<PcFormProps> = (props: PcFormProps) => {
  const [errored, setErrored] = React.useState(false);
  const [warned, setWarned] = React.useState(false);
  const maxChars = 300;
  const [charCount, setCharCount] = React.useState(maxChars);
  const formValues = professionalConnectFormSchema.cast();
  // convert the server record to professional connect form
  Object.keys(props.profile).forEach((prop: string) => {
    // eslint-disable-next-line no-prototype-builtins
    if (props.profile[prop] !== undefined && formValues.hasOwnProperty(prop)) {
      (formValues as any)[prop] = props.profile[prop];
    }
  });
  const updateCharCount = (e: any): void => {
    const chars = e.target && e.target.value ? e.target.value.length : 0;
    setCharCount(chars > 0 ? maxChars - chars : maxChars);
  };

  return (
    <div className="pc-form">
      <Formik
        initialValues={formValues}
        validationSchema={professionalConnectFormSchema}
        enableReinitialize={false}
        onReset={async (values, { setSubmitting }): Promise<void> => {
          setErrored(false);
          setSubmitting(true);
          try {
            const newValues = { ...values };
            newValues.pc = false;
            await AdminService.updateProfessionalConnect(newValues, props.slug);
            ReactGA.event({
              category: "profile",
              action: "professional-connect",
              label: props.slug,
            });
            props.cb(false);
          } catch (error) {
            console.error(error);
            setErrored(true);
            setSubmitting(false);
          }
        }}
        onSubmit={async (values, { setSubmitting }): Promise<void> => {
          setErrored(false);
          setWarned(false);
          setSubmitting(true);
          try {
            const newValues = { ...values };
            newValues.pc = true;
            const totalProps =
              (newValues.pcInterests ? newValues.pcInterests.length : 0) +
              (newValues.pcReferrals ? newValues.pcReferrals.length : 0) +
              (newValues.pcRequests ? newValues.pcRequests.length : 0) +
              (newValues.pcServices ? newValues.pcServices.length : 0);
            if (totalProps === 0) {
              setWarned(true);
              return;
            }
            await AdminService.updateProfessionalConnect(newValues, props.slug);
            ReactGA.event({
              category: "profile",
              action: "professional-connect",
              label: props.slug,
            });
            props.cb(true);
          } catch (error) {
            console.error(error);
            setErrored(true);
            setSubmitting(false);
          }
        }}
      >
        {(formik): ReactNode => (
          <>
            <Row>
              <Col xs="12">
                <Form role="form" onSubmit={formik.handleSubmit}>
                  <FormGroup className="mb-4">
                    <div className="fw500 mb-2">I'm open to network with professionals about:</div>
                    <FormikButtonList
                      options={pcInterests.map((p: string) => {
                        return { value: p, label: p };
                      })}
                      value={formik.values.pcInterests}
                      name="pcInterests"
                    />
                    <ErrorMessage name="pcInterests" component="div" className="position-relative form-feedback" />
                  </FormGroup>
                  <FormGroup className="mb-4">
                    <div className="fw500 mb-2">I am open to client referrals for:</div>
                    <FormikButtonList
                      options={pcReferrals.map((p) => {
                        return { value: p, label: p };
                      })}
                      value={formik.values.pcReferrals}
                      name="pcReferrals"
                    />
                    <ErrorMessage name="pcReferrals" component="div" className="position-relative form-feedback" />
                  </FormGroup>
                  <FormGroup className="mb-4">
                    <div className="fw500 mb-2">I provide the following services for professionals:</div>
                    <FormikButtonList
                      options={pcServices.map((p) => {
                        return { value: p, label: p };
                      })}
                      value={formik.values.pcServices}
                      name="pcServices"
                    />
                    <ErrorMessage name="pcServices" component="div" className="position-relative form-feedback" />
                  </FormGroup>
                  <FormGroup className="mb-4">
                    <div className="fw500 mb-2">I am looking for a:</div>
                    <FormikButtonList
                      options={pcRequests.map((p) => {
                        return { value: p, label: p };
                      })}
                      value={formik.values.pcRequests}
                      name="pcRequests"
                    />
                    <ErrorMessage name="pcRequests" component="div" className="position-relative form-feedback" />
                  </FormGroup>
                  <FormGroup className="mb-4">
                    <div className="fw500">Would you like to share more? </div>
                    <div className="mt-2">
                      <Field
                        placeholder="Share notes with your professional colleagues about what you're looking for or have to offer!"
                        type="text"
                        component="textarea"
                        rows="5"
                        name="pcNotes"
                        className="form-control"
                        onKeyUp={updateCharCount}
                      />
                      <div className="help-text">{charCount} characters remaining</div>
                    </div>
                  </FormGroup>
                  <div className="pc-desc pt-2 pb-2">
                    <span className="fw400">Your privacy is important to us.</span> Only practitioners in our network
                    can search and view Professional Connect profiles. Go to the Licensed Provider directory page, look
                    out for the <span className="icon-link me-1"></span>Professional Connect icon and use the search
                    engine to find professionals open to networking. You'll receive an email when a colleague is
                    interested in connecting.
                  </div>
                  <div className="text-center">
                    {warned && (
                      <FormGroup>
                        <div className="form-feedback mt-2">Please choose some topics to network about!</div>
                      </FormGroup>
                    )}
                    {errored && (
                      <FormGroup>
                        <div className="form-feedback mt-2">
                          There was an error submitting your request, please try again!
                        </div>
                      </FormGroup>
                    )}
                  </div>
                  <div className="d-flex">
                    {formik.values.pc === true && (
                      <a
                        onClick={(): void => {
                          formik.resetForm();
                        }}
                        style={{ pointerEvents: formik.isSubmitting === true ? "none" : "auto" }}
                        className={"cursor-pointer align-self-center"}
                      >
                        Delete from Profile
                      </a>
                    )}
                    <Button className="my-4 ms-auto" color="secondary" type="submit" disabled={formik.isSubmitting}>
                      {formik.values.pc === true && <>Save</>}
                      {(formik.values.pc === false || formik.values.pc === undefined) && <>Add to Profile</>}
                    </Button>
                  </div>
                </Form>
              </Col>
            </Row>
          </>
        )}
      </Formik>
    </div>
  );
};
